import React, { useState, useEffect, useCallback } from 'react';
import { customFetch } from '../apiClient';

// ── Helpers ──────────────────────────────────────────────────
const fmt = (n) =>
  n == null ? '0.00' : Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtDateTime = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true,
  });
};

const today     = () => new Date().toISOString().split('T')[0];
const monthStart = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().split('T')[0];
};

// ── Dept config (icon + colors) ───────────────────────────────
const DEPT_CONFIG = {
  'OPD Registration':         { icon: 'bi-person-plus-fill',      bg: '#e0f2fe', color: '#0284c7', border: '#bae6fd' },
  'IPD Admission':            { icon: 'bi-hospital-fill',          bg: '#f0fdf4', color: '#16a34a', border: '#bbf7d0' },
  'IPD Advance':              { icon: 'bi-cash-coin',              bg: '#f5f3ff', color: '#7c3aed', border: '#ddd6fe' },
  'Discharge Bill':           { icon: 'bi-file-earmark-check-fill',bg: '#fff7ed', color: '#d97706', border: '#fed7aa' },
  'General Billing':          { icon: 'bi-receipt-cutoff',         bg: '#eff6ff', color: '#2563eb', border: '#bfdbfe' },
  'Due Collection':           { icon: 'bi-collection-fill',        bg: '#fdf4ff', color: '#9333ea', border: '#f0abfc' },
  'Due Collection (Pharmacy)':{ icon: 'bi-collection-fill',        bg: '#f3e8ff', color: '#7c3aed', border: '#c4b5fd' },
  'Pharmacy Sale':            { icon: 'bi-capsule-pill',           bg: '#f0fdfa', color: '#0d9488', border: '#99f6e4' },
  'General Expense':          { icon: 'bi-arrow-up-right-circle-fill', bg: '#fff1f2', color: '#e11d48', border: '#fecdd3' },
  'Vendor Payment':           { icon: 'bi-shop-window',            bg: '#fef3c7', color: '#b45309', border: '#fde68a' },
};
const getDeptCfg = (dept) =>
  DEPT_CONFIG[dept] || { icon: 'bi-grid-fill', bg: '#f1f5f9', color: '#475569', border: '#e2e8f0' };


// ── DeptRow ─────────────────────────────────────────────────
const DeptRow = ({ dept, transactions }) => {
  const [open, setOpen] = useState(false);
  const cfg   = getDeptCfg(dept.department);
  const txns  = transactions.filter(t => t.department === dept.department);

  return (
    <>
      <tr
        className={`dept-row${open ? ' dept-row-open' : ''}`}
        onClick={() => setOpen(p => !p)}
        style={{ cursor: 'pointer' }}
      >
        <td style={{ width: 28, textAlign: 'center', paddingRight: 0 }}>
          <span className="expand-icon">
            <i className={`bi ${open ? 'bi-dash-circle-fill' : 'bi-plus-circle-fill'}`}
               style={{ fontSize: 13, color: open ? '#dc2626' : 'var(--theme-color, #0088cc)' }} />
          </span>
        </td>
        <td>
          <div className="dept-name-cell">
            <span className="dept-icon-wrap" style={{ background: cfg.bg, border: `1px solid ${cfg.border}` }}>
              <i className={`bi ${cfg.icon}`} style={{ color: cfg.color, fontSize: 12 }} />
            </span>
            <span className="dept-label" style={{ color: cfg.color }}>{dept.department}</span>
            <span className="txn-count-badge">{dept.transactionCount} txn{dept.transactionCount !== 1 ? 's' : ''}</span>
            <span className={`dept-type-pill ${dept.deptType === 'INCOME' ? 'pill-income' : dept.deptType === 'EXPENSE' ? 'pill-expense' : 'pill-mixed'}`}>
              {dept.deptType}
            </span>
          </div>
        </td>
        <td className="amt-cell credit-amt">
          {dept.totalCredit > 0 ? <>₹ {fmt(dept.totalCredit)}</> : <span className="dash">—</span>}
        </td>
        <td className="amt-cell debit-amt">
          {dept.totalDebit > 0 ? <>₹ {fmt(dept.totalDebit)}</> : <span className="dash">—</span>}
        </td>
        <td className={`amt-cell ${dept.netBalance >= 0 ? 'net-positive' : 'net-negative'}`}>
          ₹ {fmt(Math.abs(dept.netBalance))}
          <span className="net-sign">{dept.netBalance >= 0 ? ' ▲' : ' ▼'}</span>
        </td>
      </tr>

      {open && (
        <tr className="detail-row">
          <td colSpan={5} style={{ padding: 0 }}>
            <div className="detail-wrap">
              {txns.length === 0 ? (
                <div className="no-detail">No transactions found</div>
              ) : (
                <table className="detail-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Date & Time</th>
                      <th>Description</th>
                      <th>Pay Mode</th>
                      <th className="text-end">Credit ₹</th>
                      <th className="text-end">Debit ₹</th>
                      <th>Entry By</th>
                    </tr>
                  </thead>
                  <tbody>
                    {txns.map((t, i) => (
                      <tr key={i}>
                        <td className="srno">{i + 1}</td>
                        <td className="dt-cell">{fmtDateTime(t.transactionDate)}</td>
                        <td className="desc-cell">{t.description}</td>
                        <td>
                          <span className="mode-badge">{t.payMode || '—'}</span>
                        </td>
                        <td className="text-end">
                          {t.credit > 0
                            ? <span className="d-credit">₹ {fmt(t.credit)}</span>
                            : <span className="dash">—</span>}
                        </td>
                        <td className="text-end">
                          {t.debit > 0
                            ? <span className="d-debit">₹ {fmt(t.debit)}</span>
                            : <span className="dash">—</span>}
                        </td>
                        <td className="entryby">{t.entryBy || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={4} className="sub-total-label">Sub Total ({txns.length})</td>
                      <td className="text-end sub-credit">₹ {fmt(dept.totalCredit)}</td>
                      <td className="text-end sub-debit">₹ {fmt(dept.totalDebit)}</td>
                      <td />
                    </tr>
                  </tfoot>
                </table>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
};


// ── Main Component ────────────────────────────────────────────
const FinancialLedger = () => {
  const [fromDate,     setFromDate]     = useState(monthStart());
  const [toDate,       setToDate]       = useState(today());
  const [grandSummary, setGrandSummary] = useState({ grandTotalCredit: 0, grandTotalDebit: 0, grandBalance: 0, totalTransactions: 0 });
  const [deptSummary,  setDeptSummary]  = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading,      setLoading]      = useState(false);
  const [error,        setError]        = useState('');

  const fetchLedger = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await customFetch(`/FinancialReport/ledger?fromDate=${fromDate}&toDate=${toDate}`);
      if (!res.ok) throw new Error(await res.text() || 'Failed to fetch');
      const data = await res.json();
      setGrandSummary(data.grandSummary  || { grandTotalCredit: 0, grandTotalDebit: 0, grandBalance: 0, totalTransactions: 0 });
      setDeptSummary(data.deptSummary   || []);
      setTransactions(data.transactions || []);
    } catch (e) {
      setError(e.message || 'An error occurred.');
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate]);

  useEffect(() => { fetchLedger(); }, []);

  const incomeDepts  = deptSummary.filter(d => d.deptType === 'INCOME');
  const expenseDepts = deptSummary.filter(d => d.deptType === 'EXPENSE');
  const mixedDepts   = deptSummary.filter(d => d.deptType === 'MIXED');

  const allOrdered   = [...incomeDepts, ...expenseDepts, ...mixedDepts];

  return (
    <div className="fl-wrap">

      <div className="fl-header d-print-none">
        <div className="fl-header-left">
          <div className="fl-header-icon"><i className="bi bi-journal-text" /></div>
          <div>
            <div className="fl-title">Financial Ledger (Cashbook)</div>
            <div className="fl-sub">Department-wise inflows &amp; outflows</div>
          </div>
        </div>
        <button className="fl-print-btn" onClick={() => window.print()}>
          <i className="bi bi-printer-fill" /> Print
        </button>
      </div>

      <div className="fl-filters d-print-none">
        <div className="fl-filter-row">
          <div className="fl-filter-group">
            <label>From Date</label>
            <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className="fl-input" />
          </div>
          <div className="fl-filter-group">
            <label>To Date</label>
            <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} className="fl-input" />
          </div>
          <div className="fl-filter-group">
            <label>&nbsp;</label>
            <button className="fl-search-btn" onClick={fetchLedger} disabled={loading}>
              {loading
                ? <span className="fl-spinner" />
                : <><i className="bi bi-search" /> Search</>}
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="fl-error d-print-none">
          <i className="bi bi-exclamation-triangle-fill" /> {error}
          <button onClick={() => setError('')}><i className="bi bi-x-lg" /></button>
        </div>
      )}

      <div className="fl-cards">
        <div className="fl-card income-card">
          <div className="fl-card-icon"><i className="bi bi-arrow-down-left-circle-fill" /></div>
          <div>
            <div className="fl-card-label">Total Income (Credit)</div>
            <div className="fl-card-amt">₹ {fmt(grandSummary.grandTotalCredit)}</div>
            <div className="fl-card-sub">{incomeDepts.length} income source{incomeDepts.length !== 1 ? 's' : ''}</div>
          </div>
        </div>
        <div className="fl-card expense-card">
          <div className="fl-card-icon"><i className="bi bi-arrow-up-right-circle-fill" /></div>
          <div>
            <div className="fl-card-label">Total Expense (Debit)</div>
            <div className="fl-card-amt">₹ {fmt(grandSummary.grandTotalDebit)}</div>
            <div className="fl-card-sub">{expenseDepts.length} expense source{expenseDepts.length !== 1 ? 's' : ''}</div>
          </div>
        </div>
        <div className={`fl-card ${grandSummary.grandBalance >= 0 ? 'balance-pos-card' : 'balance-neg-card'}`}>
          <div className="fl-card-icon"><i className="bi bi-wallet2" /></div>
          <div>
            <div className="fl-card-label">Net Balance</div>
            <div className="fl-card-amt">₹ {fmt(grandSummary.grandBalance)}</div>
            <div className="fl-card-sub">{grandSummary.totalTransactions} total transactions</div>
          </div>
        </div>
      </div>

      <div className="fl-table-wrap">

        {incomeDepts.length > 0 && (
          <div className="fl-section-header income-section">
            <i className="bi bi-arrow-down-left-circle-fill" />
            Inflows — Income / Credit
            <span className="section-total">₹ {fmt(incomeDepts.reduce((s, d) => s + d.totalCredit, 0))}</span>
          </div>
        )}

        <table className="fl-table">
          <thead>
            <tr>
              <th style={{ width: 28 }} />
              <th>Department</th>
              <th className="text-end" style={{ width: 140 }}>Credit (In) ₹</th>
              <th className="text-end" style={{ width: 140 }}>Debit (Out) ₹</th>
              <th className="text-end" style={{ width: 140 }}>Net Balance ₹</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={5} className="fl-loading">
                  <span className="fl-spinner fl-spinner-dark" /> Loading...
                </td>
              </tr>
            ) : allOrdered.length === 0 ? (
              <tr>
                <td colSpan={5} className="fl-empty">
                  <i className="bi bi-inbox" /> No transactions found for the selected period.
                </td>
              </tr>
            ) : (
              <>
                {incomeDepts.map((dept, i) => (
                  <DeptRow key={`inc-${i}`} dept={dept} transactions={transactions} />
                ))}

                {expenseDepts.length > 0 && (
                  <tr className="section-divider-row">
                    <td colSpan={5}>
                      <div className="fl-section-header expense-section" style={{ margin: 0 }}>
                        <i className="bi bi-arrow-up-right-circle-fill" />
                        Outflows — Expenses / Debit
                        <span className="section-total">₹ {fmt(expenseDepts.reduce((s, d) => s + d.totalDebit, 0))}</span>
                      </div>
                    </td>
                  </tr>
                )}

                {expenseDepts.map((dept, i) => (
                  <DeptRow key={`exp-${i}`} dept={dept} transactions={transactions} />
                ))}

                {mixedDepts.map((dept, i) => (
                  <DeptRow key={`mix-${i}`} dept={dept} transactions={transactions} />
                ))}
              </>
            )}
          </tbody>

          {!loading && allOrdered.length > 0 && (
            <tfoot>
              <tr className="grand-total-row">
                <td colSpan={2} className="grand-label">
                  <i className="bi bi-calculator-fill" /> GRAND TOTAL
                  <span className="grand-count">({grandSummary.totalTransactions} txns)</span>
                </td>
                <td className="text-end grand-credit">₹ {fmt(grandSummary.grandTotalCredit)}</td>
                <td className="text-end grand-debit">₹ {fmt(grandSummary.grandTotalDebit)}</td>
                <td className={`text-end ${grandSummary.grandBalance >= 0 ? 'grand-pos' : 'grand-neg'}`}>
                  ₹ {fmt(grandSummary.grandBalance)}
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=DM+Mono:wght@400;500;600&display=swap');

        .fl-wrap { font-family: 'Plus Jakarta Sans', sans-serif; background: #f8fafc; min-height: 100vh; padding: 0 0 30px; font-size: 11px; }

        .fl-header { display: flex; align-items: center; justify-content: space-between; padding: 10px 16px; background: var(--theme-color, #0088cc); color: white; }
        .fl-header-left { display: flex; align-items: center; gap: 10px; }
        .fl-header-icon { width: 32px; height: 32px; background: rgba(255,255,255,0.15); border-radius: 6px; display: flex; align-items: center; justify-content: center; font-size: 16px; }
        .fl-title { font-size: 13px; font-weight: 700; }
        .fl-sub { font-size: 10px; color: rgba(255,255,255,0.85); margin-top: 1px; }
        .fl-print-btn { padding: 4px 12px; border-radius: 4px; border: 1px solid rgba(255,255,255,0.4); background: rgba(255,255,255,0.1); color: #fff; font-size: 11px; font-weight: 600; cursor: pointer; display: flex; align-items: center; gap: 6px; transition: background .15s; }
        .fl-print-btn:hover { background: rgba(255,255,255,0.25); }

        .fl-filters { background: #fff; padding: 10px 16px; border-bottom: 1px solid #e2e8f0; }
        .fl-filter-row { display: flex; gap: 12px; align-items: flex-end; flex-wrap: wrap; }
        .fl-filter-group { display: flex; flex-direction: column; gap: 4px; }
        .fl-filter-group label { font-size: 10px; font-weight: 600; color: #64748b; }
        .fl-input { height: 28px; padding: 0 8px; border: 1px solid #cbd5e1; border-radius: 4px; font-size: 11px; color: #0f172a; background: #fff; outline: none; transition: border-color .15s; }
        .fl-input:focus { border-color: var(--theme-color, #0088cc); }
        .fl-search-btn { height: 28px; padding: 0 16px; border-radius: 4px; border: none; background: var(--theme-color, #0088cc); color: #fff; font-size: 11px; font-weight: 600; cursor: pointer; display: flex; align-items: center; gap: 4px; transition: opacity .15s; }
        .fl-search-btn:hover:not(:disabled) { opacity: .9; }
        .fl-search-btn:disabled { opacity: .6; cursor: not-allowed; }

        .fl-spinner { display: inline-block; width: 12px; height: 12px; border: 2px solid rgba(255,255,255,.4); border-top-color: #fff; border-radius: 50%; animation: fl-spin .7s linear infinite; }
        .fl-spinner-dark { border-color: rgba(0,0,0,.15); border-top-color: var(--theme-color, #0088cc); }
        @keyframes fl-spin { to { transform: rotate(360deg); } }

        .fl-error { margin: 10px 16px 0; padding: 8px 12px; background: #fef2f2; border: 1px solid #fecaca; border-radius: 6px; font-size: 11px; color: #991b1b; display: flex; align-items: center; gap: 6px; }
        .fl-error button { margin-left: auto; background: none; border: none; color: #991b1b; cursor: pointer; font-size: 14px; }

        .fl-cards { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; padding: 12px 16px; }
        .fl-card { display: flex; align-items: center; gap: 12px; padding: 12px; border-radius: 8px; border: 1px solid #e2e8f0; background: #fff; }
        .fl-card-icon { font-size: 20px; }
        .fl-card-label { font-size: 10px; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: .5px; }
        .fl-card-amt { font-size: 16px; font-weight: 700; font-family: 'DM Mono', monospace; margin: 2px 0; }
        .fl-card-sub { font-size: 9px; color: #94a3b8; }

        .income-card { border-bottom: 3px solid #10b981; }
        .income-card .fl-card-icon { color: #059669; }
        .income-card .fl-card-amt { color: #059669; }

        .expense-card { border-bottom: 3px solid #ef4444; }
        .expense-card .fl-card-icon { color: #dc2626; }
        .expense-card .fl-card-amt { color: #dc2626; }

        .balance-pos-card { border-bottom: 3px solid #3b82f6; }
        .balance-pos-card .fl-card-icon { color: #2563eb; }
        .balance-pos-card .fl-card-amt { color: #1d4ed8; }

        .balance-neg-card { border-bottom: 3px solid #f43f5e; }
        .balance-neg-card .fl-card-icon { color: #e11d48; }
        .balance-neg-card .fl-card-amt { color: #be123c; }

        .fl-table-wrap { margin: 0 16px; background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; overflow: hidden; }

        .fl-section-header { display: flex; align-items: center; gap: 6px; padding: 6px 12px; font-size: 11px; font-weight: 700; }
        .income-section { background: #f0fdf4; color: #166534; border-bottom: 1px solid #bbf7d0; }
        .expense-section { background: #fff1f2; color: #9f1239; border-top: 1px solid #fecdd3; border-bottom: 1px solid #fecdd3; }
        .section-total { margin-left: auto; font-family: 'DM Mono', monospace; font-size: 11px; }
        .section-divider-row td { padding: 0 !important; }

        .fl-table { width: 100%; border-collapse: collapse; font-size: 11px; }
        .fl-table thead tr { background: #f1f5f9; }
        .fl-table th { padding: 8px 10px; text-align: left; font-size: 10px; font-weight: 700; color: #475569; text-transform: uppercase; letter-spacing: .5px; border-bottom: 1px solid #e2e8f0; white-space: nowrap; }
        .text-end { text-align: right !important; }

        .dept-row { background: #fff; transition: background .15s; border-bottom: 1px solid #f1f5f9; }
        .dept-row:hover { background: #f8fafc; }
        .dept-row-open { background: #f0f9ff !important; }
        .dept-row td { padding: 8px 10px; vertical-align: middle; }
        .dept-name-cell { display: flex; align-items: center; gap: 8px; }
        .dept-icon-wrap { width: 24px; height: 24px; border-radius: 5px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
        .dept-label { font-size: 11.5px; font-weight: 700; }
        .txn-count-badge { font-size: 9.5px; font-weight: 600; background: #e0f2fe; color: #0284c7; border: 1px solid #bae6fd; padding: 1px 6px; border-radius: 8px; white-space: nowrap; }
        .dept-type-pill { font-size: 9.5px; font-weight: 700; padding: 1px 6px; border-radius: 8px; white-space: nowrap; }
        .pill-income { background: #dcfce7; color: #166534; border: 1px solid #bbf7d0; }
        .pill-expense { background: #fee2e2; color: #991b1b; border: 1px solid #fecaca; }
        .pill-mixed { background: #f3e8ff; color: #6b21a8; border: 1px solid #e9d5ff; }

        .amt-cell { font-family: 'DM Mono', monospace; font-size: 12px; font-weight: 600; text-align: right; white-space: nowrap; }
        .credit-amt { color: #059669; }
        .debit-amt { color: #dc2626; }
        .net-positive { color: #0284c7; }
        .net-negative { color: #e11d48; }
        .net-sign { font-size: 9px; }
        .dash { color: #94a3b8; }

        .detail-row { background: #f8fafc; }
        .detail-wrap { padding: 0 10px 10px 42px; }
        .no-detail { font-size: 11px; color: #94a3b8; padding: 10px 0; font-style: italic; }

        .detail-table { width: 100%; border-collapse: collapse; font-size: 10px; margin-top: 6px; border: 1px solid #e2e8f0; border-radius: 6px; overflow: hidden; }
        .detail-table thead tr { background: #e0f2fe; }
        .detail-table th { padding: 6px 8px; text-align: left; font-size: 9.5px; font-weight: 700; color: #0369a1; text-transform: uppercase; letter-spacing: .4px; border-bottom: 1px solid #bae6fd; white-space: nowrap; }
        .detail-table td { padding: 5px 8px; border-bottom: 1px solid #f1f5f9; color: #0f172a; vertical-align: middle; }
        .detail-table tbody tr:last-child td { border-bottom: none; }
        .detail-table tbody tr:hover { background: #f0f9ff; }
        .detail-table tfoot tr { background: #f1f5f9; }
        .detail-table tfoot td { padding: 6px 8px; font-weight: 700; border-top: 1px solid #e2e8f0; }

        .srno { color: #94a3b8; font-family: 'DM Mono', monospace; font-size: 9.5px; width: 24px; }
        .dt-cell { white-space: nowrap; color: #64748b; }
        .desc-cell { max-width: 260px; }
        .mode-badge { background: #f1f5f9; color: #475569; border: 1px solid #e2e8f0; border-radius: 4px; padding: 2px 6px; font-size: 9.5px; font-weight: 600; white-space: nowrap; }
        .d-credit { color: #059669; font-family: 'DM Mono', monospace; font-weight: 600; }
        .d-debit { color: #dc2626; font-family: 'DM Mono', monospace; font-weight: 600; }
        .entryby { color: #64748b; font-size: 9.5px; }
        .sub-total-label { color: #64748b; font-size: 10px; text-align: right; }
        .sub-credit { color: #059669; font-family: 'DM Mono', monospace; }
        .sub-debit { color: #dc2626; font-family: 'DM Mono', monospace; }

        .grand-total-row { background: color-mix(in srgb, var(--theme-color, #0088cc) 8%, #f8fafc); border-top: 2px solid var(--theme-color, #0088cc); }
        .grand-total-row td { padding: 10px 10px; }
        .grand-label { color: var(--theme-color, #0088cc); font-size: 11.5px; font-weight: 700; display: flex; align-items: center; gap: 8px; text-transform: uppercase;}
        .grand-count { color: #64748b; font-size: 9.5px; font-weight: 600; text-transform: none; }
        .grand-credit { color: #059669; font-family: 'DM Mono', monospace; font-size: 13px; font-weight: 700; text-align: right; }
        .grand-debit { color: #dc2626; font-family: 'DM Mono', monospace; font-size: 13px; font-weight: 700; text-align: right; }
        .grand-pos { color: #0284c7; font-family: 'DM Mono', monospace; font-size: 13px; font-weight: 700; text-align: right; }
        .grand-neg { color: #e11d48; font-family: 'DM Mono', monospace; font-size: 13px; font-weight: 700; text-align: right; }

        .fl-loading { text-align: center; padding: 24px; color: #64748b; font-size: 11.5px; display: flex; align-items: center; justify-content: center; gap: 8px; }
        .fl-empty { text-align: center; padding: 32px; color: #94a3b8; font-size: 11.5px; }
        .fl-empty i { font-size: 20px; display: block; margin-bottom: 6px; }

        @media print {
          body * { visibility: hidden; }
          .fl-wrap, .fl-wrap * { visibility: visible; }
          .fl-wrap { position: absolute; left: 0; top: 0; width: 100%; padding: 0; background: #fff; }
          .d-print-none { display: none !important; }
          .fl-cards { break-inside: avoid; }
          .fl-table-wrap { border: none; margin: 0; }
          .detail-wrap { padding-left: 10px; }
          .grand-total-row { background: #f1f5f9 !important; border-top: 2px solid #000; }
        }

        @media (max-width: 700px) {
          .fl-cards { grid-template-columns: 1fr; }
          .fl-filter-row { flex-direction: column; }
        }
      `}</style>
    </div>
  );
};

export default FinancialLedger;